import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "index";
import { apiRequest } from "utils/api";
import { useTenant } from "modules/tenant/providers/tenant-provider";

const NotificationsContext = createContext<any | null>(null);

const NotificationsConsumer = NotificationsContext.Consumer;

function NotificationsProvider({ children }: { children: JSX.Element }) {
  const { user } = useTenant();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unread, setUnread] = useState<any[]>([]);

  const { pathname } = useLocation();
  const pathnameRef = useRef<string>(pathname);

  async function handleNewNotification(payload: any) {
    const { id, name, conversation } = payload.new;
    if (name === "sent_message" && pathnameRef.current.includes(conversation)) {
      await dismissNotification(id);
    } else if (name === "sent_message_capsource" && pathnameRef.current.includes(conversation)) {
      await dismissNotification(id);
    } else {
      loadNotifications();
    }
  }

  async function loadNotifications() {
    try {
      let response = await apiRequest("/supabase/notifications/select");
      const unreadNotifications = (response.notifications || []).filter(
        (n: any) => n.name !== "sent_message"
      );
      const unreadMessages = (response.notifications || []).filter(
        (n: any) => n.name === "sent_message"
      );
      setNotifications(unreadNotifications);
      setUnread(unreadMessages);
    } catch (err) {
      console.error(err);
    }
  }

  async function dismissNotification(id: string) {
    try {
      await apiRequest("/supabase/notifications/update", {
        id,
        dismissed: true,
      });

      setNotifications(notifications.filter(notification => id !== notification.id));
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    loadNotifications();
    const notificationsFilter = `participant=eq.${user.id}`;
    const subscriptionFilter = {
      schema: "public",
      table: "notifications",
      filter: notificationsFilter,
    };
    const channel = supabase
      .channel(`public:notifications:${notificationsFilter}`)
      .on(
        "postgres_changes",
        {
          ...subscriptionFilter,
          event: "INSERT",
        },
        handleNewNotification
      )
      .on(
        "postgres_changes",
        {
          ...subscriptionFilter,
          event: "UPDATE",
        },
        loadNotifications
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  if (!user) {
    return null;
  }

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unread,
        loadNotifications,
        dismissNotification,
      }}
    >
      <>{children}</>
    </NotificationsContext.Provider>
  );
}
export { NotificationsContext, NotificationsConsumer, NotificationsProvider };
export const useAlertsContext = () => useContext(NotificationsContext);
